import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import { useParams, useNavigate } from "react-router-dom";
import {
  executeLessonValidation,
  handleLessonInputChangeWithValidation,
  handleLessonResetWithValidation,
  clearLessonValidationErrors,
} from "../../../utils/formValidation";

// 曜日の定義（表示順）
const DAYS_OF_WEEK = [
  { key: "monday", label: "月" },
  { key: "tuesday", label: "火" },
  { key: "wednesday", label: "水" },
  { key: "thursday", label: "木" },
  { key: "friday", label: "金" },
  { key: "saturday", label: "土" },
  { key: "sunday", label: "日" },
];

/**
 * 保存済みレッスンのIDを返す。
 * 新規追加分は 'new_xxx' / 'space_xxx' の仮IDなのでnullを返す。
 */
const getExistingLessonId = (lesson) => {
  const id = lesson?.id;
  if (typeof id === "number") return id;
  if (typeof id === "string" && /^\d+$/.test(id)) return Number(id);
  return null;
};

/**
 * レッスンに紐づく体験予約・在籍会員の件数を取得する。
 * 取得に失敗した場合はnullを返し、呼び出し側では件数不明として扱う。
 */
const fetchLessonReferences = async (lessonId) => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/check_lesson_references/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lesson_id: lessonId }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error("レッスンの参照チェックに失敗しました", error);
    return null;
  }
};

/**
 * 削除確認ダイアログの文言を組み立てる。
 * 紐付いているデータがある場合は、その件数と影響を明示する。
 */
const buildDeleteConfirmMessage = (lesson, references) => {
  const lessonLabel = lesson.name
    ? `「${lesson.name}」`
    : "このレッスン";

  if (!references) {
    return (
      `${lessonLabel}を削除します。\n\n` +
      "※紐付いている体験予約・会員の確認ができませんでした。\n\n" +
      "削除してもよろしいですか？"
    );
  }

  const warnings = [];
  if (references.member_count > 0) {
    warnings.push(`在籍中の会員 ${references.member_count}名`);
  }
  if (references.upcoming_trial_count > 0) {
    warnings.push(`今後の体験予約 ${references.upcoming_trial_count}件`);
  }

  if (warnings.length === 0) {
    return `${lessonLabel}を削除します。\n\n削除してもよろしいですか？`;
  }

  return (
    `${lessonLabel}には ${warnings.join("、")} が紐づいています。\n` +
    "削除すると、これらの紐付けが外れます。\n\n" +
    "曜日や時間を変更したいだけの場合は、削除せずに\n" +
    "カード内の曜日セレクトや時間の編集で変更してください。\n\n" +
    "それでも削除してよろしいですか？"
  );
};

// スケジュールテーブルコンポーネント
function ScheduleTable({
  lessonsData,
  instructorsList,
  onLessonsChange,
  onHasChanges,
}) {
  const daysOfWeek = DAYS_OF_WEEK;

  // レッスン追加（指定したインデックスの後に追加）
  const addLesson = (dayKey, afterIndex = -1) => {
    const newLesson = {
      id: `new_${Date.now()}`,
      name: "",
      start_time: "",
      end_time: "",
      day_of_week: dayKey,
      instructor_id: null,
      instructor_name: "",
      image: null,
      space_flg: false,
      order: 0,
      manualImageSelected: false,
      close_flg: false,
      beginner_flg: false,
    };

    const currentLessons = lessonsData[dayKey] || [];
    const updatedLessons = { ...lessonsData };

    if (afterIndex === -1) {
      // 最後に追加
      updatedLessons[dayKey] = [...currentLessons, newLesson];
    } else if (afterIndex === -2) {
      // 最初に追加（曜日ヘッダーからの場合）
      updatedLessons[dayKey] = [newLesson, ...currentLessons];
    } else {
      // 指定したインデックスの後に挿入
      const newArray = [...currentLessons];
      newArray.splice(afterIndex + 1, 0, newLesson);
      updatedLessons[dayKey] = newArray;
    }

    onLessonsChange(updatedLessons);
    onHasChanges(true);
  };

  // スペース追加（指定したインデックスの後に追加）
  const addSpace = (dayKey, afterIndex = -1) => {
    const newSpace = {
      id: `space_${Date.now()}`,
      name: "",
      start_time: "",
      end_time: "",
      day_of_week: dayKey,
      instructor_id: null,
      instructor_name: "",
      space_flg: true,
      order: 0,
      close_flg: false,
    };

    const currentLessons = lessonsData[dayKey] || [];
    const updatedLessons = { ...lessonsData };

    if (afterIndex === -1) {
      // 最後に追加
      updatedLessons[dayKey] = [...currentLessons, newSpace];
    } else if (afterIndex === -2) {
      // 最初に追加（曜日ヘッダーからの場合）
      updatedLessons[dayKey] = [newSpace, ...currentLessons];
    } else {
      // 指定したインデックスの後に挿入
      const newArray = [...currentLessons];
      newArray.splice(afterIndex + 1, 0, newSpace);
      updatedLessons[dayKey] = newArray;
    }

    onLessonsChange(updatedLessons);
    onHasChanges(true);
  };

  // レッスンを別の曜日へ移動（IDを維持したまま day_of_week だけを変更する）
  // 「削除して追加し直す」とIDが変わり、体験予約や会員の紐付けが外れてしまうため、
  // 曜日を変えたいだけの場合はこの移動を使う。
  const moveLessonToDay = (fromDayKey, lessonIndex, toDayKey) => {
    if (!toDayKey || fromDayKey === toDayKey) return;

    const fromLessons = [...(lessonsData[fromDayKey] || [])];
    const [movingLesson] = fromLessons.splice(lessonIndex, 1);
    if (!movingLesson) return;

    const toLessons = [...(lessonsData[toDayKey] || [])];

    // 移動先では開始時間順の位置に挿入する（時間未入力・スペースは末尾）
    let insertAt = toLessons.length;
    const movingStartTime = movingLesson.start_time || "";
    if (movingStartTime) {
      const foundIndex = toLessons.findIndex(
        (lesson) =>
          !lesson.space_flg && (lesson.start_time || "") > movingStartTime
      );
      if (foundIndex !== -1) insertAt = foundIndex;
    }
    toLessons.splice(insertAt, 0, { ...movingLesson, day_of_week: toDayKey });

    // インデックスがずれるため、既存のエラー表示はクリアする
    clearLessonValidationErrors();

    onLessonsChange({
      ...lessonsData,
      [fromDayKey]: fromLessons,
      [toDayKey]: toLessons,
    });
    onHasChanges(true);
  };

  // レッスン削除
  // 保存済みのレッスンは、体験予約や在籍会員が紐づいていないかを確認してから削除する。
  const deleteLesson = async (dayKey, lessonIndex) => {
    const lesson = (lessonsData[dayKey] || [])[lessonIndex];
    if (!lesson) return;

    const existingId = getExistingLessonId(lesson);

    // 未保存のレッスン（まだDBに存在しない）と、
    // 体験予約・会員が紐づき得ないスペース行はそのまま削除する
    if (existingId && !lesson.space_flg) {
      const references = await fetchLessonReferences(existingId);
      if (!window.confirm(buildDeleteConfirmMessage(lesson, references))) {
        return;
      }
    }

    // インデックスがずれるため、既存のエラー表示はクリアする
    clearLessonValidationErrors();

    const updatedLessons = {
      ...lessonsData,
      [dayKey]: lessonsData[dayKey].filter((_, index) => index !== lessonIndex),
    };

    onLessonsChange(updatedLessons);
    onHasChanges(true);
  };

  // レッスン更新
  const updateLesson = (dayKey, lessonIndex, field, value) => {
    console.log(field, value);

    // バリデーションエラークリア処理
    handleLessonInputChangeWithValidation(dayKey, lessonIndex, field);

    const updatedLessons = { ...lessonsData };
    updatedLessons[dayKey][lessonIndex][field] = value;

    // インストラクター選択時の画像設定
    if (field === "instructor_id" && value) {
      const currentLesson = updatedLessons[dayKey][lessonIndex];

      // 手動で画像が選択されていない場合のみインストラクターの画像を設定
      if (!currentLesson.manualImageSelected) {
        const instructor = instructorsList.find(
          (inst) => inst.id === parseInt(value)
        );
        if (
          instructor &&
          instructor.top_images &&
          instructor.top_images.length > 0
        ) {
          updatedLessons[dayKey][lessonIndex].image = instructor.top_images[0];
        } else {
          // インストラクターに画像がない場合は画像をクリア
          updatedLessons[dayKey][lessonIndex].image = null;
        }
      }
    }

    onLessonsChange(updatedLessons);
    onHasChanges(true);
  };

  // 画像選択ハンドラー
  const handleImageSelect = (dayKey, lessonIndex, image) => {
    const updatedLessons = { ...lessonsData };
    updatedLessons[dayKey][lessonIndex].image = image;
    // 手動で画像が選択されたことをフラグで記録
    updatedLessons[dayKey][lessonIndex].manualImageSelected = true;
    onLessonsChange(updatedLessons);
    onHasChanges(true);
  };

  // レッスン画像を取得する関数
  const getLessonImage = (lesson) => {
    // 1. レッスンに画像が登録されている場合はそれを返す
    // if (lesson.image) {
    //   return lesson.image;
    // }

    // 2. インストラクターが選択されている場合、そのtop_images[0]を返す
    if (lesson.instructor_id) {
      const instructor = instructorsList.find(
        (inst) => inst.id === parseInt(lesson.instructor_id)
      );
      if (
        instructor &&
        instructor.top_images &&
        instructor.top_images.length > 0
      ) {
        return instructor.top_images[0];
      }
    }

    // 3. どちらもない場合はnullを返す
    return null;
  };

  return (
    <div className="schedule-table">
      <div className="schedule-grid">
        {daysOfWeek.map((day) => (
          <div key={day.key} className="day-column">
            <div className="day-header">
              <h3>{day.label}</h3>
            </div>

            <div className="lessons-list">
              <div className="day-actions">
                <button
                  type="button"
                  className="add"
                  onClick={() => addLesson(day.key, -2)}
                >
                  + Add
                </button>
                <button
                  type="button"
                  className="space"
                  onClick={() => addSpace(day.key, -2)}
                >
                  Space
                </button>
              </div>
              {(lessonsData[day.key] || []).map((lesson, index) => (
                <div
                  key={lesson.id || index}
                  className={`lesson-item ${lesson.space_flg ? "space" : ""}`}
                  data-day={day.key}
                  data-index={index}
                >
                  {lesson.space_flg ? (
                    <div className="lesson-content space">
                      <div className="space-item">
                        <span>Space</span>
                      </div>
                    </div>
                  ) : (
                    <div className="lesson-content">
                      <div className="lesson-form">
                        <div className="form-inputs">
                          <div className="time-inputs">
                            <select
                              className="day-select"
                              name={`${day.key}_${index}_day_of_week`}
                              value={day.key}
                              onChange={(e) =>
                                moveLessonToDay(day.key, index, e.target.value)
                              }
                              title="曜日を変更（レッスンIDは維持されます）"
                            >
                              {daysOfWeek.map((d) => (
                                <option key={d.key} value={d.key}>
                                  {d.label}
                                </option>
                              ))}
                            </select>
                            <input
                              type="time"
                              name={`${day.key}_${index}_start_time`}
                              value={lesson.start_time || ""}
                              onChange={(e) =>
                                updateLesson(
                                  day.key,
                                  index,
                                  "start_time",
                                  e.target.value
                                )
                              }
                              placeholder="開始時間"
                              data-label="開始時間"
                              data-required="true"
                            />
                            <span>-</span>
                            <input
                              type="time"
                              name={`${day.key}_${index}_end_time`}
                              value={lesson.end_time || ""}
                              onChange={(e) =>
                                updateLesson(
                                  day.key,
                                  index,
                                  "end_time",
                                  e.target.value
                                )
                              }
                              placeholder="終了時間"
                              data-label="終了時間"
                              data-required="true"
                            />
                          </div>

                          <select
                            name={`${day.key}_${index}_instructor_id`}
                            value={lesson.instructor_id || ""}
                            onChange={(e) =>
                              updateLesson(
                                day.key,
                                index,
                                "instructor_id",
                                e.target.value
                              )
                            }
                            data-label="インストラクター"
                            data-required="true"
                          >
                            <option value="">インストラクターを選択</option>
                            {instructorsList.map((instructor) => (
                              <option key={instructor.id} value={instructor.id}>
                                {instructor.dancer_name}
                              </option>
                            ))}
                          </select>

                          <input
                            type="text"
                            name={`${day.key}_${index}_name`}
                            value={lesson.name || ""}
                            onChange={(e) =>
                              updateLesson(
                                day.key,
                                index,
                                "name",
                                e.target.value
                              )
                            }
                            placeholder="レッスン名"
                            data-label="レッスン名"
                            data-required="true"
                          />

                          <div className="close-flag-input">
                            <label>
                              <input
                                type="checkbox"
                                checked={lesson.close_flg || false}
                                onChange={(e) =>
                                  updateLesson(
                                    day.key,
                                    index,
                                    "close_flg",
                                    e.target.checked
                                  )
                                }
                              />
                              受付停止
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={lesson.beginner_flg || false}
                                onChange={(e) =>
                                  updateLesson(
                                    day.key,
                                    index,
                                    "beginner_flg",
                                    e.target.checked
                                  )
                                }
                              />
                              初心者
                            </label>
                          </div>
                        </div>

                        <div className="image-section">
                          <MediaSelector
                            label=""
                            aspectRatio=""
                            type="image"
                            fieldName={`lesson_image_${day.key}_${index}`}
                            selectedMedia={getLessonImage(lesson)}
                            onSelect={(image) =>
                              handleImageSelect(day.key, index, image)
                            }
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className={
                      lesson.space_flg ? "space-actions" : "lesson-actions"
                    }
                  >
                    <button
                      type="button"
                      className="add"
                      onClick={() => addLesson(day.key, index)}
                    >
                      + Add
                    </button>
                    <button
                      type="button"
                      className="space"
                      onClick={() => addSpace(day.key, index)}
                    >
                      Space
                    </button>
                    <button
                      type="button"
                      className="delete"
                      onClick={() => deleteLesson(day.key, index)}
                    >
                      × Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// パンくず関数
const getBreadcrumbs = (studioId, studiosList) => {
  const baseBreadcrumbs = [{ title: "Home", path: `/` }];

  if (studioId && studiosList.length > 0) {
    const studio = studiosList.find((s) => s.id === parseInt(studioId));
    if (studio) {
      baseBreadcrumbs.push({
        title: studio.slug,
        path: `/lesson/${studioId}`,
      });
    }
  }

  return baseBreadcrumbs;
};

function LessonEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // スタジオリスト
  const [studiosList, setStudiosList] = useState([]);
  // インストラクターリスト
  const [instructorsList, setInstructorsList] = useState([]);
  // レッスンデータ
  const [lessonsData, setLessonsData] = useState({});
  // 選択中のスタジオ
  const [selectedStudioId, setSelectedStudioId] = useState(null);

  // 新規作成モード
  const [isNewStudio, setIsNewStudio] = useState(false);

  // レッスン説明（全店舗共通）
  const [lessonDescription, setLessonDescription] = useState("");
  const [descriptionChanged, setDescriptionChanged] = useState(false);

  const { studioId } = useParams();
  const navigate = useNavigate();

  // スタジオリストの取得
  const fetchStudiosList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStudiosList(data);
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
    }
  }, []);

  // インストラクターリストの取得
  const fetchInstructorsList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_instructors_for_lesson_page/`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setInstructorsList(data);
    } catch (error) {
      console.error("インストラクターリストの取得に失敗しました", error);
    }
  }, []);

  // レッスンデータの取得
  const fetchLessonsData = useCallback(async (studioId) => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_lessons/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ studio_id: studioId }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // 曜日ごとにグループ化
      const groupedLessons = {};
      const daysOfWeek = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday",
      ];

      daysOfWeek.forEach((day) => {
        groupedLessons[day] = data
          .filter((lesson) => lesson.day_of_week === day)
          .sort((a, b) => a.order - b.order)
          .map((lesson) => ({
            ...lesson,
            // DBから取得した既存レッスンで画像がある場合のみ手動選択済みとみなす
            // 画像がない場合は手動選択されていないとして扱う
            manualImageSelected: lesson.image ? true : false,
          }));
      });

      setLessonsData(groupedLessons);
      setLoadingText("");
    } catch (error) {
      console.error("レッスンデータの取得に失敗しました", error);
      setLoadingText("");
    }
  }, []);

  // レッスン説明の取得
  const fetchLessonDescription = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_page_lesson_content/`,
        { method: "GET", credentials: "include" }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setLessonDescription(data.description || "");
      setDescriptionChanged(false);
    } catch (error) {
      console.error("レッスン説明の取得に失敗しました", error);
    }
  }, []);

  // レッスン説明の保存
  const saveLessonDescription = async () => {
    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_lesson_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ description: lessonDescription }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setMessage(data.message);
      setDescriptionChanged(false);
    } catch (error) {
      console.error("レッスン説明の保存に失敗しました", error);
    }
    setLoadingText("");
  };

  // 初回のみ実行（スタジオリストとメディアファイル取得）
  useEffect(() => {
    fetchStudiosList();
    fetchInstructorsList();
    fetchLessonDescription();
    fetchMediaFiles();
  }, [fetchStudiosList, fetchInstructorsList, fetchLessonDescription, fetchMediaFiles]);

  // studioIdが変更されたときにレッスンデータを取得
  useEffect(() => {
    const fetchData = async () => {
      if (studioId) {
        const studioIdNum = parseInt(studioId);
        if (studioIdNum !== selectedStudioId) {
          setSelectedStudioId(studioIdNum);
          await fetchLessonsData(studioIdNum);
          setIsNewStudio(false);
        }
      } else {
        // studioIdがない場合はクリア
        setSelectedStudioId(null);
        setLessonsData({});
        setIsNewStudio(false);
      }
    };
    fetchData();
  }, [studioId, selectedStudioId, fetchLessonsData]);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }

      setLoadingText("アップロード中");

      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };

          // メディアファイル一覧を再取得
          await fetchMediaFiles();

          // fetchMediaFiles完了後に選択状態にする
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // バリデーションルールを定義
  const validationRules = {
    start_time: { required: true },
    end_time: { required: true },
    instructor_id: { required: true },
    name: { required: true },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    start_time: "開始時間",
    end_time: "終了時間",
    instructor_id: "インストラクター",
    name: "レッスン名",
  };

  // レッスンデータの保存
  const saveLessonsData = useCallback(async () => {
    if (!selectedStudioId || !lessonsData) return;

    // バリデーション実行
    if (!executeLessonValidation(lessonsData, validationRules, fieldLabels)) {
      return;
    }

    setLoadingText("保存中");

    try {
      // レッスンデータを配列に変換
      const allLessons = [];
      Object.keys(lessonsData).forEach((dayKey) => {
        lessonsData[dayKey].forEach((lesson, index) => {
          const lessonData = {
            ...lesson,
            day_of_week: dayKey,
            order: index,
          };

          // レッスンに画像が設定されていない場合、インストラクターの画像を設定
          if (!lessonData.image && lessonData.instructor_id) {
            const instructor = instructorsList.find(
              (inst) => inst.id === parseInt(lessonData.instructor_id)
            );
            if (
              instructor &&
              instructor.top_images &&
              instructor.top_images.length > 0
            ) {
              lessonData.image = instructor.top_images[0];
            }
          }

          allLessons.push(lessonData);
        });
      });

      console.log(allLessons);

      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/bulk_save_lessons/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studio_id: selectedStudioId,
          lessons: allLessons,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      setMessage("レッスン情報を保存しました。");
      setHasChanges(false);

      // 保存後にデータを再取得
      await fetchLessonsData(selectedStudioId);
    } catch (error) {
      console.error("レッスンデータの保存に失敗しました", error);
      setMessage("保存に失敗しました。");
    } finally {
      setLoadingText("");
    }
  }, [selectedStudioId, lessonsData, fetchLessonsData]);

  // リセット機能
  const resetLessonsData = useCallback(async () => {
    handleLessonResetWithValidation(async () => {
      setLoadingText("リセット中");
      if (selectedStudioId) {
        await fetchLessonsData(selectedStudioId);
        setHasChanges(false);
        setMessage("");
      }
      setLoadingText("");
    });
  }, [selectedStudioId, fetchLessonsData]);

  return (
    <>
      <div className={`editarea schedule ${isMediaOpen ? "media" : ""}`}>
        {/* パンくず */}
        <Breadcrumbs breadcrumbs={getBreadcrumbs(studioId, studiosList)} />

        <div className={`message ${message ? "success" : ""}`}>
          <p>{message}</p>
        </div>

        {/* レッスン説明（全店舗共通） */}
        <div className="form">
          <section>
            <div className="area1">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h5 style={{ margin: 0 }}>レッスン説明（全店舗共通）</h5>
                <button
                  type="button"
                  className="description-save-btn"
                  onClick={saveLessonDescription}
                  disabled={!descriptionChanged}
                >
                  説明を保存
                </button>
              </div>
              <textarea
                className="input2"
                value={lessonDescription}
                onChange={(e) => {
                  setLessonDescription(e.target.value);
                  setDescriptionChanged(true);
                }}
                rows={10}
                placeholder="レッスンの説明を入力してください"
              />
            </div>
          </section>
        </div>

        {/* スタジオリスト */}
        <div className="form">
          <div className="studio-list">
            <ul>
              {studiosList.map((studio) => (
                <li
                  key={studio.id}
                  className={
                    parseInt(studioId) === studio.id && !isNewStudio
                      ? "active"
                      : ""
                  }
                  onClick={() => {
                    navigate(`/lesson/${studio.id}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {studio.name}
                </li>
              ))}
            </ul>
          </div>
          {/* スケジュールテーブル */}
          {selectedStudioId && (
            <ScheduleTable
              lessonsData={lessonsData}
              instructorsList={instructorsList}
              onLessonsChange={setLessonsData}
              onHasChanges={setHasChanges}
            />
          )}
        </div>
        {/* 送信ボタン */}
        {selectedStudioId && (
          <div className="submit-area">
            <button
              type="reset"
              onClick={resetLessonsData}
              disabled={!hasChanges}
            >
              Reset
            </button>
            <button
              type="submit"
              onClick={saveLessonsData}
              disabled={!hasChanges}
            >
              Submit
            </button>
          </div>
        )}
        {/* メディア / オーバーレイ */}
        {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
      </div>
      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function LessonEditPage() {
  return (
    <MediaProvider>
      <LessonEditPageContent />
    </MediaProvider>
  );
}
